import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../../utils/api.js";
import { parseUTC } from "../../utils/date.js";

interface AssignmentRow {
  id: number;
  title: string;
  pdfPath: string;
  notes: string | null;
  timelineDays: number;
  uploadedByName: string;
  createdAt: string;
}

export function AssignmentList() {
  const [assignments, setAssignments] = useState<AssignmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AssignmentRow | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();

  const fetchAssignments = useCallback(() => {
    api
      .get<{ results: AssignmentRow[] }>("/assignment-library")
      .then((d) => setAssignments(d.results))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load assignments"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  useEffect(() => {
    const msg = searchParams.get("success");
    if (msg) {
      setToast(msg);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/assignment-library/${deleteTarget.id}`);
      setDeleteTarget(null);
      fetchAssignments();
      setToast(`"${deleteTarget.title}" deleted`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete assignment");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {toast && (
        <div className="rounded-md bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800">
          {toast}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Assignments</h1>
          <p className="text-sm text-gray-500 mt-1">Manage take-home assignment templates</p>
        </div>
        <Link
          to="/assignments/new"
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-[var(--action-primary-hover)] transition-colors"
        >
          New Assignment
        </Link>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-gray-400 animate-pulse">Loading...</div>
      ) : assignments.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-gray-500">
          No assignments yet.{" "}
          <Link to="/assignments/new" className="text-primary-700 hover:text-primary-800">
            Upload your first assignment
          </Link>
          .
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Timeline</th>
                <th className="px-4 py-3">Uploaded By</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {assignments.map((a) => (
                <tr key={a.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <Link
                      to={`/assignments/${a.id}/edit`}
                      className="font-medium text-primary-700 hover:text-primary-900"
                    >
                      {a.title}
                    </Link>
                    {a.notes && (
                      <div className="text-xs text-gray-400 truncate max-w-xs">{a.notes}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {a.timelineDays} day{a.timelineDays !== 1 ? "s" : ""}
                  </td>
                  <td className="px-4 py-3 text-gray-600 text-xs">{a.uploadedByName}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs">
                    {parseUTC(a.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <a
                        href={`/uploads/${a.pdfPath}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs font-medium text-gray-400 hover:text-gray-600"
                      >
                        Preview
                      </a>
                      <Link
                        to={`/assignments/${a.id}/edit`}
                        className="text-xs font-medium text-primary-700 hover:text-primary-800"
                      >
                        Edit
                      </Link>
                      <button
                        onClick={() => setDeleteTarget(a)}
                        className="text-xs font-medium text-red-500 hover:text-red-700"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Delete assignment</h2>
            <p className="text-sm text-gray-600">
              Are you sure you want to delete <strong>{deleteTarget.title}</strong>?
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
